import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listRecipes, deleteRecipe, runRecipe, setFeedCuttingSimulator, bridgeNow, exportRecipeTemplateUrl, importRecipeTemplate } from "@/lib/api";
import { formatRelative, formatAbsolute, formatClock, shortUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Play, Edit, Trash2, BookOpen, Search, Calendar, CalendarRange, Clock, Scissors, Zap, Download, Upload, Archive } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import ScheduleDialog from "@/components/recipes/ScheduleDialog";
import RunsHistoryDialog from "@/components/recipes/RunsHistoryDialog";
import DateRangeRunDialog from "@/components/recipes/DateRangeRunDialog";
import DaysPickerRunDialog from "@/components/recipes/DaysPickerRunDialog";
import IntervalRunDialog from "@/components/recipes/IntervalRunDialog";
import CloneRecipeDialog from "@/components/recipes/CloneRecipeDialog";
import TemplateRecipeDialog from "@/components/recipes/TemplateRecipeDialog";

const STATUS_PT = { success: "sucesso", partial: "parcial", error: "erro", running: "a correr" };

export default function RecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [runningId, setRunningId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const nav = useNavigate();

  async function refresh() {
    setLoading(true);
    try { setRecipes(await listRecipes()); } catch (e) { toast.error("Falha ao carregar receitas"); } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  async function onRun(r) {
    setRunningId(r.id);
    try {
      const run = await runRecipe(r.id);
      if (run.status === 'success') { toast.success(`Captura concluída: ${run.rows?.length || 0} linhas`); nav(`/runs/${run.id}`); }
      else { toast.error(`A captura falhou: ${run.error || 'erro desconhecido'}`); nav(`/runs/${run.id}`); }
    } catch (e) {
      toast.error(`A captura falhou: ${e?.response?.data?.detail || e.message}`);
    } finally { setRunningId(null); refresh(); }
  }
  async function onDelete(r) {
    try { await deleteRecipe(r.id); toast.success("Receita apagada"); refresh(); } catch (e) { toast.error("Falha ao apagar"); }
  }

  async function onToggleFeed(r, enabled) {
    try {
      await setFeedCuttingSimulator(r.id, enabled);
      toast.success(enabled ? "Esta receita vai alimentar a Máquina de Corte" : "Ligação à Máquina de Corte desligada");
      refresh();
    } catch (e) {
      toast.error(`Falhou: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function onBridgeNow(r) {
    try {
      const res = await bridgeNow(r.id);
      if (res?.error) {
        toast.error(`Bridge falhou: ${res.error}`);
      } else if (res?.skipped) {
        toast.info("A última captura não tem registos.");
      } else {
        toast.success(`Cutting Simulator actualizado: ${res.total_cuts || 0} cortes em ${res.date}`);
      }
    } catch (e) {
      toast.error(`Falhou: ${e?.response?.data?.detail || e.message}`);
    }
  }

  function onExport(r) {
    // Open in a new tab so the browser handles the download via Content-Disposition
    const url = exportRecipeTemplateUrl(r.id);
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    // Suggest a filename hint (browser still honors server's Content-Disposition)
    const safe = (r.name || "recipe").replace(/[^a-zA-Z0-9_-]+/g, "_");
    a.download = `recipe_${safe}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(`Exportado: ${r.name}`);
  }

  function onClickImport() {
    fileInputRef.current?.click();
  }

  async function onFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-picked
    if (!file) return;
    if (!/\.json$/i.test(file.name)) {
      toast.error("Por favor selecione um ficheiro .json");
      return;
    }
    setImporting(true);
    try {
      const text = await file.text();
      let payload;
      try { payload = JSON.parse(text); } catch { throw new Error("Ficheiro JSON inválido"); }
      if (!payload || typeof payload !== "object") throw new Error("JSON não contém um modelo válido");
      if (!payload.start_url || !Array.isArray(payload.steps)) {
        throw new Error("Modelo inválido: faltam campos 'start_url' ou 'steps'");
      }
      const created = await importRecipeTemplate({
        name: payload.name,
        start_url: payload.start_url,
        steps: payload.steps,
        notes: payload.notes ?? null,
        schedule: payload.schedule ?? null,
        convert_times: !!payload.convert_times,
        feed_cutting_simulator: !!payload.feed_cutting_simulator,
      });
      toast.success(`Receita importada: ${created.name}`);
      await refresh();
    } catch (err) {
      toast.error(`Importação falhou: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setImporting(false);
    }
  }

  const filtered = recipes.filter(r => !q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.start_url||'').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{fontFamily:'Space Grotesk'}}>Receitas</h1>
          <p className="text-sm text-muted-foreground mt-1">Receitas de recolha guardadas que pode voltar a correr sempre que quiser.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:flex-none min-w-[180px]">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2"/>
            <Input data-testid="recipe-library-search-input" placeholder="Procurar por nome ou URL…" value={q} onChange={e => setQ(e.target.value)} className="pl-9 w-full sm:w-[260px]"/>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={onFilePicked}
            data-testid="recipe-library-import-input"
          />
          <Button
            data-testid="recipe-library-import-button"
            variant="outline"
            onClick={onClickImport}
            disabled={importing}
            title="Importar receita a partir de um ficheiro .json"
          >
            <Upload className="h-4 w-4 mr-1"/> <span className="hidden xs:inline">{importing ? "A importar…" : "Importar"}</span><span className="xs:hidden">{importing ? "…" : ""}</span>
          </Button>
          <Button data-testid="recipe-library-template-button" variant="outline" onClick={() => setTplOpen(true)} title="Criar receita a partir do modelo padrão arquivado">
            <Archive className="h-4 w-4 mr-1"/> <span className="hidden xs:inline">Modelo padrão</span>
          </Button>
          <TemplateRecipeDialog open={tplOpen} onOpenChange={setTplOpen} onCreated={() => refresh()}/>
          <Button data-testid="recipe-library-new-recipe-button" onClick={() => nav('/recorder')}>
            <Plus className="h-4 w-4 mr-1"/> <span className="hidden xs:inline">Nova receita</span><span className="xs:hidden">Nova</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Card key={i} className="h-44 animate-pulse bg-muted/40"/>)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState onCreate={() => nav('/recorder')} hasQuery={!!q}/>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(r => (
            <Card key={r.id} data-testid="recipe-card" className="p-5 flex flex-col gap-3 hover:shadow-md transition-shadow duration-150">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-base font-semibold truncate" title={r.name}>{r.name}</div>
                  <div className="mono text-xs text-muted-foreground truncate mt-1" title={r.start_url}>{shortUrl(r.start_url)}</div>
                </div>
                <Badge variant="outline" className="shrink-0">{r.steps?.length || 0} passos</Badge>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.schedule?.enabled && (
                  <Badge variant="outline" className="gap-1 bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30" data-testid="recipe-schedule-badge">
                    <Clock className="h-3 w-3"/> {r.schedule.start_time}–{r.schedule.end_time} · {r.schedule.interval_minutes}min
                  </Badge>
                )}
                {r.convert_times && (
                  <Badge variant="outline" className="bg-muted text-muted-foreground border-border" data-testid="recipe-convert-times-badge">
                    tempos→min
                  </Badge>
                )}
                {(r.steps || []).some(s => s.type === 'fill' && s.is_date) && (
                  <Badge variant="outline" className="gap-1 bg-accent text-accent-foreground border-border" data-testid="recipe-has-date-badge">
                    <Calendar className="h-3 w-3"/> datas
                  </Badge>
                )}
                {r.feed_cutting_simulator && (
                  <Badge variant="outline" className="gap-1 bg-emerald-500/15 text-emerald-700 border-emerald-500/30" data-testid="recipe-feed-cutting-badge">
                    <Scissors className="h-3 w-3"/> alimenta Cutting
                  </Badge>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Scissors className="h-3.5 w-3.5 text-emerald-600"/>
                  <span>Alimentar Máquina de Corte</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!r.feed_cutting_simulator}
                    onCheckedChange={(v) => onToggleFeed(r, v)}
                    data-testid="recipe-feed-cutting-switch"
                  />
                  {r.feed_cutting_simulator && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onBridgeNow(r)} data-testid="recipe-bridge-now-button" title="Reenviar para a Máquina de Corte a última captura com sucesso">
                      <Zap className="h-3 w-3 mr-1"/> agora
                    </Button>
                  )}
                </div>
              </div>
              {r.schedule?.enabled ? (
                <div className="grid grid-cols-2 gap-2 rounded-lg border border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/5 p-3" data-testid="recipe-schedule-times">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3"/> Última leitura
                    </div>
                    {r.last_run_at ? (
                      <>
                        <div className={`text-sm font-semibold ${r.last_run_status === 'success' ? 'text-emerald-600' : r.last_run_status === 'partial' ? 'text-amber-600' : 'text-destructive'}`} data-testid="recipe-last-run-time">
                          {formatClock(r.last_run_at)} <span className="text-muted-foreground font-normal">· {formatRelative(r.last_run_at)}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate" title={formatAbsolute(r.last_run_at)}>{formatAbsolute(r.last_run_at)} · {STATUS_PT[r.last_run_status] || r.last_run_status}</div>
                      </>
                    ) : (
                      <div className="text-sm text-muted-foreground">nunca</div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3"/> Próxima leitura
                    </div>
                    {r.next_run_at ? (
                      <>
                        <div className="text-sm font-semibold text-primary" data-testid="recipe-next-run-time">
                          {formatClock(r.next_run_at)} <span className="text-muted-foreground font-normal">· {formatRelative(r.next_run_at)}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate" title={formatAbsolute(r.next_run_at)}>{formatAbsolute(r.next_run_at)}</div>
                      </>
                    ) : (
                      <div className="text-sm text-muted-foreground">—</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Última: {r.last_run_at ? `${formatRelative(r.last_run_at)} • ${STATUS_PT[r.last_run_status] || r.last_run_status || "—"}` : 'nunca'}
                </div>
              )}
              <div className="mt-auto flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                <Button data-testid="recipe-card-run-button" size="sm" onClick={() => onRun(r)} disabled={runningId === r.id}>
                  <Play className="h-3.5 w-3.5 mr-1"/> {runningId === r.id ? 'A correr…' : 'Correr'}
                </Button>
                <DateRangeRunDialog recipe={r}/>
                <DaysPickerRunDialog recipe={r}/>
                <IntervalRunDialog recipe={r}/>
                <RunsHistoryDialog recipe={r}/>
                <ScheduleDialog recipe={r} onUpdated={refresh} triggerLabel={r.schedule?.enabled ? 'Agenda ✓' : 'Agendar'}/>
                <CloneRecipeDialog recipe={r} onCloned={refresh}/>
                <Button data-testid="recipe-card-edit-button" size="sm" variant="outline" asChild>
                  <Link to={`/recorder/${r.id}`}><Edit className="h-3.5 w-3.5 mr-1"/> Editar</Link>
                </Button>
                <Button
                  data-testid="recipe-card-export-button"
                  size="sm"
                  variant="outline"
                  onClick={() => onExport(r)}
                  title="Exportar receita para ficheiro .json"
                >
                  <Download className="h-3.5 w-3.5 mr-1"/> Exportar
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="recipe-card-delete-button" size="sm" variant="ghost" className="ml-auto text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4"/>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Apagar “{r.name}”?</AlertDialogTitle>
                      <AlertDialogDescription>Apaga a receita e todas as suas capturas permanentemente.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onDelete(r)} data-testid="recipe-card-delete-confirm-button">Apagar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onCreate, hasQuery }) {
  return (
    <Card className="p-10 noise-overlay bg-[radial-gradient(900px_circle_at_20%_0%,hsl(var(--accent))_0%,transparent_55%)]">
      <div className="max-w-lg">
        <div className="h-12 w-12 rounded-xl bg-primary/15 text-primary grid place-items-center mb-4">
          <BookOpen className="h-6 w-6"/>
        </div>
        <h2 className="text-2xl font-semibold tracking-tight" style={{fontFamily:'Space Grotesk'}}>{hasQuery ? 'Nenhuma receita encontrada' : 'Ainda não há receitas'}</h2>
        <p className="text-sm text-muted-foreground mt-2">
          {hasQuery ? 'Experimente outra pesquisa.' : 'Grave a sua primeira receita de recolha. Abra um URL, clique nos botões necessários e termine com um passo “Extrair tabela”.'}
        </p>
        <Button className="mt-5" onClick={onCreate} data-testid="empty-state-new-recipe-button">
          <Plus className="h-4 w-4 mr-1"/> Criar a primeira receita
        </Button>
      </div>
    </Card>
  );
}
